// Phase I-B.1 — Admin Project Schema management.
//
// Lives as a separate tab inside `/app/frontend/src/pages/AdminFiltersTab.jsx`
// ("Listing" vs "Project" scopes are visually distinct sub-sections).
//
// Read/write the single-source `/api/admin/project-schema` API. NO
// hard-coded option arrays; every dropdown is fed by `projectSchemaApi`.
//
// Sub-tabs:
//   • Core Project Fields     — locked stable keys, editable display metadata
//   • Configuration Types     — admin CRUD (add/edit/archive)
//   • Amenities               — admin CRUD (add/edit/archive)
//   • Project Statuses        — locked stable keys; label/sort/active editable
//   • Possession Filter Presets — locked stable keys; label/sort/active editable

import React, { useEffect, useMemo, useState } from "react";
import {
  fetchProjectSchemaAdmin,
  adminPatchProjectField,
  adminCreateProjectCatalogOption,
  adminPatchProjectCatalogOption,
  adminArchiveProjectCatalogOption,
  _resetProjectSchemaCache,
} from "../lib/projectSchemaApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Lock, Plus, Save, RotateCcw, Archive, ArchiveRestore, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMsg } from "../lib/api";

const PROJECT_SUBTABS = [
  { key: "fields",         label: "Core Project Fields" },
  { key: "configuration_type", label: "Configuration Types" },
  { key: "amenity",        label: "Amenities" },
  { key: "project_status", label: "Project Statuses" },
  { key: "possession_window", label: "Possession Presets" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Top-level component
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminProjectSchemaTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSub, setActiveSub] = useState("fields");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchProjectSchemaAdmin();
      setData(d);
    } catch (e) {
      setError(apiErrorMsg(e, "Failed to load Project schema"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    _resetProjectSchemaCache();
    await load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-zinc-500 text-[13px]" data-testid="project-schema-loading">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading Project schema…
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-sm p-4 text-[12.5px] text-red-800" data-testid="project-schema-error">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold mb-1">Could not load Project schema</div>
            <div className="text-red-700">{error}</div>
            <Button onClick={refresh} variant="outline" size="sm" className="mt-3 rounded-sm" data-testid="project-schema-retry">
              <RotateCcw size={12} className="mr-1.5" /> Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-project-schema">
      <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2">
        <Lock size={12} className="mt-0.5 shrink-0" />
        <div>
          <strong>Project scope</strong> — these definitions power the Project
          form &amp; public project filters only. Listing-side fields are
          managed in the other two tabs above and are NEVER affected by
          changes here.
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto" data-testid="project-schema-subtabs">
        {PROJECT_SUBTABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveSub(t.key)}
            className={`px-4 py-2 text-[12.5px] font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeSub === t.key
                ? "border-[#0055FF] text-[#0055FF]"
                : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
            data-testid={`project-schema-subtab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeSub === "fields" ? (
        <CoreFieldsPanel
          fields={data.fields}
          lockedFieldKeys={data.locked_field_keys}
          onPatched={refresh}
        />
      ) : (
        <CatalogPanel
          scope={activeSub}
          options={(data.catalog_options || []).filter((o) => o.scope === activeSub)}
          locked={(data.locked_catalog_scopes || []).includes(activeSub)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Core Fields editor
// ─────────────────────────────────────────────────────────────────────────────

function CoreFieldsPanel({ fields, lockedFieldKeys, onPatched }) {
  const [savingKey, setSavingKey] = useState(null);
  const lockedSet = useMemo(() => new Set(lockedFieldKeys || []), [lockedFieldKeys]);

  const save = async (field, patch) => {
    setSavingKey(field.key);
    try {
      // Phase I-B.1 — `project_name` cannot be disabled or made
      // optional. Block client-side too as a defence in depth.
      if (field.key === "project_name") {
        if (patch.required === false) {
          toast.error("'project_name' cannot be made optional.");
          return;
        }
        if (patch.show_on_form === false) {
          toast.error("'project_name' cannot be removed from the form.");
          return;
        }
        if (patch.active === false) {
          toast.error("'project_name' cannot be deactivated.");
          return;
        }
      }
      await adminPatchProjectField(field.key, patch);
      toast.success(`Saved ${field.label}`);
      await onPatched();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="core-fields-panel">
      <div className="text-[11.5px] text-zinc-500 mb-1">
        Display label, required-state, sort order and form/filter visibility
        are editable. Backend keys are locked and shown for reference only.
      </div>
      <table className="w-full border border-zinc-200 rounded-sm text-[12px]">
        <thead className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-wide text-zinc-500">
          <tr>
            <th className="text-left px-2 py-2">Field</th>
            <th className="text-center px-2 py-2 w-20">Active</th>
            <th className="text-center px-2 py-2 w-20">Required</th>
            <th className="text-center px-2 py-2 w-24">On Form</th>
            <th className="text-center px-2 py-2 w-24">In Filters</th>
            <th className="text-center px-2 py-2 w-20">Sort</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              locked={lockedSet.has(f.key)}
              saving={savingKey === f.key}
              onSave={(patch) => save(f, patch)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FieldRow({ field, locked, saving, onSave }) {
  const [draft, setDraft] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const merged = { ...field, ...draft };
  const dirty = Object.keys(draft).length > 0;
  const isProjectName = field.key === "project_name";

  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <>
      <tr className="border-t border-zinc-200" data-testid={`field-row-${field.key}`}>
        <td className="px-2 py-2">
          <div className="flex items-center gap-1.5">
            {locked && <Lock size={11} className="text-zinc-400" />}
            <div className="flex-1 min-w-0">
              <Input
                value={merged.label}
                onChange={(e) => change("label", e.target.value)}
                className="h-7 text-[12px] rounded-sm"
                data-testid={`field-row-${field.key}-label`}
              />
              <div className="text-[10px] text-zinc-400 font-mono mt-0.5">{field.key}</div>
            </div>
            <button
              onClick={() => setHelpOpen((v) => !v)}
              className="text-[10px] text-zinc-400 hover:text-zinc-700"
              data-testid={`field-row-${field.key}-help-toggle`}
            >
              ?
            </button>
          </div>
        </td>
        <td className="text-center px-2 py-2">
          <Switch
            checked={merged.active}
            onCheckedChange={(v) => change("active", v)}
            disabled={isProjectName}
            data-testid={`field-row-${field.key}-active`}
          />
        </td>
        <td className="text-center px-2 py-2">
          <Switch
            checked={merged.required}
            onCheckedChange={(v) => change("required", v)}
            disabled={isProjectName}
            data-testid={`field-row-${field.key}-required`}
          />
        </td>
        <td className="text-center px-2 py-2">
          <Switch
            checked={merged.show_on_form}
            onCheckedChange={(v) => change("show_on_form", v)}
            disabled={isProjectName}
            data-testid={`field-row-${field.key}-show-on-form`}
          />
        </td>
        <td className="text-center px-2 py-2">
          <Switch
            checked={merged.show_in_filters}
            onCheckedChange={(v) => change("show_in_filters", v)}
            data-testid={`field-row-${field.key}-show-in-filters`}
          />
        </td>
        <td className="text-center px-2 py-2">
          <Input
            type="number"
            value={merged.sort_order}
            onChange={(e) => change("sort_order", parseInt(e.target.value, 10) || 0)}
            className="h-7 text-[12px] rounded-sm w-16 mx-auto"
            data-testid={`field-row-${field.key}-sort-order`}
          />
        </td>
      </tr>
      {helpOpen && (
        <tr className="bg-[#FAFAFA] border-t border-zinc-100">
          <td colSpan={6} className="px-2 py-2">
            <Textarea
              rows={2}
              value={merged.help_text}
              onChange={(e) => change("help_text", e.target.value)}
              placeholder="Help text shown to dealer/builder under the field…"
              className="text-[11.5px] rounded-sm"
              data-testid={`field-row-${field.key}-help-text`}
            />
          </td>
        </tr>
      )}
      {dirty && (
        <tr className="bg-blue-50 border-t border-blue-100">
          <td colSpan={6} className="px-2 py-2 flex justify-end gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => setDraft({})}
              className="text-[11px] rounded-sm"
              data-testid={`field-row-${field.key}-cancel`}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => onSave(draft).then(() => setDraft({}))}
              disabled={saving}
              className="btn-brand text-[11px] rounded-sm"
              data-testid={`field-row-${field.key}-save`}
            >
              {saving ? <Loader2 size={11} className="animate-spin mr-1" /> : <Save size={11} className="mr-1" />}
              Save
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Catalog editor (configuration_type / amenity / project_status / possession_window)
// ─────────────────────────────────────────────────────────────────────────────

function CatalogPanel({ scope, options, locked, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [busyId, setBusyId] = useState(null);

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusyId("__new__");
    try {
      await adminCreateProjectCatalogOption({ scope, label });
      toast.success(`Added "${label}"`);
      setNewLabel("");
      setAdding(false);
      await onChanged();
    } catch (e) {
      // Duplicate (HTTP 409) is a user-facing error, not a code bug.
      toast.error(apiErrorMsg(e, "Could not add option"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid={`catalog-panel-${scope}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11.5px] text-zinc-500 flex-1">
          {locked ? (
            <span className="inline-flex items-start gap-1.5">
              <Lock size={11} className="mt-0.5 shrink-0 text-amber-700" />
              <span>
                <strong>Locked scope.</strong> Backend keys carry stable
                query semantics (e.g. <code>3m</code> = next-3-months) and
                cannot be added/renamed/archived. Toggle <code>active</code>
                or edit the display <code>label</code> only.
              </span>
            </span>
          ) : (
            <span>Add new options, edit labels, archive unused ones. Used options can only be archived, not deleted.</span>
          )}
        </div>
        {!locked && (
          <Button
            size="sm"
            onClick={() => setAdding((v) => !v)}
            className="rounded-sm text-[11px]"
            data-testid={`catalog-${scope}-add-toggle`}
          >
            <Plus size={11} className="mr-1" /> {adding ? "Cancel" : "Add Option"}
          </Button>
        )}
      </div>

      {adding && !locked && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-center gap-2" data-testid={`catalog-${scope}-add-row`}>
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={scope === "amenity" ? "e.g. Pickleball Court" : "Label"}
            className="h-7 text-[12px] rounded-sm flex-1"
            data-testid={`catalog-${scope}-new-label`}
          />
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={busyId === "__new__" || !newLabel.trim()}
            className="btn-brand text-[11px] rounded-sm"
            data-testid={`catalog-${scope}-confirm-add`}
          >
            {busyId === "__new__" ? <Loader2 size={11} className="animate-spin mr-1" /> : <Save size={11} className="mr-1" />}
            Save
          </Button>
        </div>
      )}

      {options.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-sm py-8 text-center text-[12px] text-zinc-500" data-testid={`catalog-${scope}-empty`}>
          No options yet. {locked ? "(Locked scopes are seeded by the migration only.)" : 'Click "Add Option" above to create one.'}
        </div>
      ) : (
        <table className="w-full border border-zinc-200 rounded-sm text-[12px]">
          <thead className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="text-left px-2 py-2">Label</th>
              <th className="text-left px-2 py-2 w-44">Backend key</th>
              <th className="text-center px-2 py-2 w-20">Sort</th>
              <th className="text-center px-2 py-2 w-24">Used</th>
              <th className="text-center px-2 py-2 w-20">Active</th>
              <th className="text-center px-2 py-2 w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {options.map((o) => (
              <CatalogRow
                key={o.id}
                option={o}
                scope={scope}
                lockedScope={locked}
                busy={busyId === o.id}
                onSetBusy={setBusyId}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CatalogRow({ option, scope, lockedScope, busy, onSetBusy, onChanged }) {
  const [draft, setDraft] = useState({});
  const merged = { ...option, ...draft };
  const dirty = Object.keys(draft).length > 0;
  const used = (option.used_count || 0) > 0;
  const isLockedRow = lockedScope || option.locked;

  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    onSetBusy(option.id);
    try {
      await adminPatchProjectCatalogOption(option.id, draft);
      toast.success(`Saved ${option.label}`);
      setDraft({});
      await onChanged();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Save failed"));
    } finally {
      onSetBusy(null);
    }
  };

  const archive = async () => {
    if (isLockedRow) {
      toast.error("Locked options cannot be archived — toggle Active off instead.");
      return;
    }
    if (used) {
      const confirm = window.confirm(
        `"${option.label}" is used by ${option.used_count} project${option.used_count === 1 ? "" : "s"}. ` +
        `Archiving will hide it from new forms but the legacy value will remain visible on existing projects. Proceed?`
      );
      if (!confirm) return;
    }
    onSetBusy(option.id);
    try {
      await adminArchiveProjectCatalogOption(option.id);
      toast.success(`Archived ${option.label}`);
      await onChanged();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Archive failed"));
    } finally {
      onSetBusy(null);
    }
  };

  const unarchive = async () => {
    onSetBusy(option.id);
    try {
      await adminPatchProjectCatalogOption(option.id, { active: true });
      toast.success(`Restored ${option.label}`);
      await onChanged();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Restore failed"));
    } finally {
      onSetBusy(null);
    }
  };

  return (
    <>
      <tr className="border-t border-zinc-200" data-testid={`catalog-row-${scope}-${option.key}`}>
        <td className="px-2 py-2">
          <div className="flex items-center gap-1.5">
            {isLockedRow && <Lock size={11} className="text-zinc-400 shrink-0" />}
            <Input
              value={merged.label}
              onChange={(e) => change("label", e.target.value)}
              className="h-7 text-[12px] rounded-sm"
              data-testid={`catalog-row-${scope}-${option.key}-label`}
            />
          </div>
        </td>
        <td className="px-2 py-2 font-mono text-[10.5px] text-zinc-500">{option.key}</td>
        <td className="text-center px-2 py-2">
          <Input
            type="number"
            value={merged.sort_order}
            onChange={(e) => change("sort_order", parseInt(e.target.value, 10) || 0)}
            className="h-7 text-[12px] rounded-sm w-16 mx-auto"
            data-testid={`catalog-row-${scope}-${option.key}-sort`}
          />
        </td>
        <td className="text-center px-2 py-2 text-zinc-600 text-[11.5px]" data-testid={`catalog-row-${scope}-${option.key}-used`}>
          {option.used_count || 0}
        </td>
        <td className="text-center px-2 py-2">
          <Switch
            checked={merged.active}
            onCheckedChange={(v) => change("active", v)}
            data-testid={`catalog-row-${scope}-${option.key}-active`}
          />
        </td>
        <td className="text-center px-2 py-2">
          {option.archived_at ? (
            <Button
              variant="ghost" size="sm"
              onClick={unarchive}
              disabled={busy}
              className="text-[11px] rounded-sm"
              data-testid={`catalog-row-${scope}-${option.key}-unarchive`}
            >
              <ArchiveRestore size={11} className="mr-1" /> Restore
            </Button>
          ) : (
            !isLockedRow && (
              <Button
                variant="ghost" size="sm"
                onClick={archive}
                disabled={busy}
                className="text-[11px] rounded-sm text-red-700 hover:text-red-900"
                data-testid={`catalog-row-${scope}-${option.key}-archive`}
              >
                <Archive size={11} className="mr-1" /> Archive
              </Button>
            )
          )}
        </td>
      </tr>
      {dirty && (
        <tr className="bg-blue-50 border-t border-blue-100">
          <td colSpan={6} className="px-2 py-2 flex justify-end gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => setDraft({})}
              className="text-[11px] rounded-sm"
              data-testid={`catalog-row-${scope}-${option.key}-cancel`}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={busy}
              className="btn-brand text-[11px] rounded-sm"
              data-testid={`catalog-row-${scope}-${option.key}-save`}
            >
              {busy ? <Loader2 size={11} className="animate-spin mr-1" /> : <Save size={11} className="mr-1" />}
              Save
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}
